// iot-admin/client/src/pages/GGOSDeviceDetail/sectionRegistry.js
// Data-driven section registry for the GGOS Device Detail page.
// Add new sections here — nav, routing, and lazy-loading are automatic.

import { lazy } from 'react';
import {
  LayoutDashboard,
  FileText,
  Wifi,
  Activity,
  Tv,
  Eye,
  ReceiptText,
  ShieldCheck,
  Package,
  Radio,
  Terminal,
  AppWindow,
  SlidersHorizontal,
  Waves,
  Map as MapIcon,
} from 'lucide-react';

// Lazy-loaded section components (code-split per section)
const SummarySection = lazy(() => import('./sections/SummarySection'));
const OverviewIdentitySection = lazy(() => import('./sections/OverviewIdentitySection'));
const ConnectionSection = lazy(() => import('./sections/ConnectionSection'));
const SnapshotSection = lazy(() => import('./sections/SnapshotSection'));
const SignalTimeline = lazy(() => import('./sections/SignalTimeline'));
const AdManifestSection = lazy(() => import('./sections/AdManifestSection'));
const ProofOfPlaySection = lazy(() => import('./sections/ProofOfPlaySection'));
const PerceptionSection = lazy(() => import('./sections/PerceptionSection'));
const InventorySection = lazy(() => import('./sections/InventorySection'));
const SecurityAccessSection = lazy(() => import('./sections/SecurityAccessSection'));
const TelemetrySection = lazy(() => import('./sections/TelemetrySection'));
const CommandsSection = lazy(() => import('./sections/CommandsSection'));
const AppsSection = lazy(() => import('./sections/AppsSection'));
const DeviceControlCenter = lazy(() => import('./sections/DeviceControlCenter'));
const MobilitySection = lazy(() => import('./sections/MobilitySection'));
const PaneSection = lazy(() => import('./sections/PaneSection'));

/**
 * Section registry — single source of truth for all device detail sections.
 *
 * Shape:
 *   id        — URL slug (used in ?section=<id>)
 *   label     — display name in left nav
 *   icon      — lucide-react icon component
 *   group     — nav group header
 *   component — lazy-loaded React component
 *
 * To add a new section: append an entry here. Everything else is automatic.
 */
const SECTION_REGISTRY = [
  // ── Overview ──
  {
    id: 'summary',
    label: 'Summary',
    icon: FileText,
    group: 'Overview',
    component: SummarySection,
  },
  {
    id: 'identity',
    label: 'Identity',
    icon: LayoutDashboard,
    group: 'Overview',
    component: OverviewIdentitySection,
  },
  {
    id: 'inventory',
    label: 'Inventory',
    icon: Package,
    group: 'Overview',
    component: InventorySection,
  },
  {
    id: 'connection',
    label: 'Connection',
    icon: Wifi,
    group: 'Overview',
    component: ConnectionSection,
  },

  // ── Live State ──
  {
    id: 'snapshot',
    label: 'Device Snapshot',
    icon: Activity,
    group: 'Live State',
    component: SnapshotSection,
  },
  {
    id: 'signal-timeline',
    label: 'Signal Timeline',
    icon: Waves,
    group: 'Live State',
    component: SignalTimeline,
  },

  // ── Advertising ──
  {
    id: 'ad-manifest',
    label: 'Ad Manifest',
    icon: Tv,
    group: 'Advertising',
    component: AdManifestSection,
  },
  {
    id: 'delivery',
    label: 'Delivery',
    icon: ReceiptText,
    group: 'Advertising',
    component: ProofOfPlaySection,
  },
  {
    id: 'perception',
    label: 'Perception',
    icon: Eye,
    group: 'Advertising',
    component: PerceptionSection,
  },

  {
    id: 'mobility',
    label: 'Mobility',
    icon: MapIcon,
    group: 'Advertising',
    component: MobilitySection,
  },
  {
    id: 'pane',
    label: 'Pane',
    icon: AppWindow,
    group: 'Advertising',
    component: PaneSection,
  },

  // ── Security & Access ──
  {
    id: 'security',
    label: 'Security & Access',
    icon: ShieldCheck,
    group: 'Security & Access',
    component: SecurityAccessSection,
  },

  // ── Telemetry ──
  {
    id: 'telemetry',
    label: 'Telemetry',
    icon: Radio,
    group: 'Telemetry',
    component: TelemetrySection,
  },

  // ── App Management ──
  {
    id: 'apps',
    label: 'Apps',
    icon: AppWindow,
    group: 'Management',
    component: AppsSection,
  },
  {
    id: 'controls',
    label: 'Device Controls',
    icon: SlidersHorizontal,
    group: 'Management',
    component: DeviceControlCenter,
  },

  // ── Commands ──
  {
    id: 'commands',
    label: 'Commands',
    icon: Terminal,
    group: 'Commands',
    component: CommandsSection,
  },
];

export const DEFAULT_SECTION = 'summary';

/**
 * Returns sections grouped by their `group` field, preserving insertion order.
 * Shape: [{ group: string, items: Section[] }]
 */
export function getGroupedSections() {
  const groups = [];
  const seen = new Map();
  for (const section of SECTION_REGISTRY) {
    if (!seen.has(section.group)) {
      const entry = { group: section.group, items: [] };
      seen.set(section.group, entry);
      groups.push(entry);
    }
    seen.get(section.group).items.push(section);
  }
  return groups;
}

export function getSectionById(id) {
  return SECTION_REGISTRY.find((s) => s.id === id) || null;
}

export default SECTION_REGISTRY;
